import { memo, MouseEventHandler, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, darken, FormControl, InputLabel, lighten, Stack, styled, Typography } from '@mui/material';
import {
  DataGridPro, DataGridProProps, getGridDateOperators, getGridNumericOperators, getGridStringOperators, GridColDef, 
  GridColumnVisibilityModel, GridEventListener, GridFilterModel, GridGroupNode, 
  GridSortModel, GridToolbarColumnsButton, GridToolbarExportProps, useGridApiRef,
  GridToolbarFilterButton, useGridApiContext, GridRenderCellParams,
  gridFilteredSortedRowIdsSelector,
  GridPaginationModel,
  GridTreeNode,
} from '@mui/x-data-grid-pro';
import { DateTime, Duration } from 'luxon';
import { 
  CabButton, CabDropdown, CabDurationPicker, CabIcon, CabSpinner, CabToggleChip, 
  CabTooltip 
} from '@CabComponents';
import { useCabinetText } from '../../../CabinetContext';
import {
  FetchMeetingsResponse, FetchReturn, Leader, Meeting, MeetingPollPriority, MeetingPriority, MeetingSlot, MeetingStatus,
  ParticipantsWithSelections, RecurringTimes
} from '../../../store';
import { colors } from '../../../colors';
import { MEETING_PRIORITY_LABELS, MEETING_STATUS_LABELS, REUSABLE_MEETING_STATUS_LABELS } from '../../../constants';
import { useMountEffect } from '../../../utils/hooks';
import { CabTextTokenDisplay } from '@CabComponents/CabTextTokenDisplay';
import { 
  TimeZone, allTimeZones, getNumResponses, getQuestionsAndAnswers, 
  getTemplateVars, getTimesOfferedText,
} from '../../../utils/scheduleUtils';
import { ActionMenu } from '../../../components/Meeting/ActionMenu';
import { transformSimpleTemplateToText } from '@CabComponents/CabTextTokenInput';
import { GridFilterInputValueProps } from "@mui/x-data-grid-pro";
import NoRowOverlay from '../../EventAnalytics/NoRowsOverlay';
import { 
  IoChevronDown, IoChevronForward, IoEllipsisHorizontal, IoTrashOutline, IoAdd, IoDownloadOutline 
} from 'react-icons/io5';
import CabDataGrid from '@CabComponents/CabDataGrid';


export type MeetingInfo = Meeting & {
  participantsInfo: ParticipantsWithSelections;
  slots: { start: DateTime, end: DateTime, priority: MeetingPollPriority }[];
  locations: string[]|null|undefined;
};

type Row = Omit<MeetingInfo, 'id'> & {
  id: number;
  hierarchy: string[];
  descendantCount?: number;
  childrenFetched?: boolean;
};

type GridSortItem = GridSortModel[number];
type GridProSlotProps = NonNullable<DataGridProProps["slotProps"]>;
type GridInitialStatePro = NonNullable<DataGridProProps["initialState"]>;

type MeetingLogToolbarProps = {
  polls: boolean;
  reusable: boolean;
  tableFilter: GridFilterModel;
  onNewMeeting: () => void;
  startExportMeetings: () => void;
  handleClearFilters: () => void;
};

const MeetingLogToolbar = memo(({
  polls,
  reusable,
  tableFilter,
  onNewMeeting,
  startExportMeetings,
  handleClearFilters
}: MeetingLogToolbarProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      width: "100%",
      marginTop: 2,
      marginBottom: 1
    }}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "row-reverse",
        flex: 1
      }}>
      {!polls && (
        <CabTooltip title={"Download table data to CSV file"} >
          <CabButton
            onClick={startExportMeetings}
            icon={<CabIcon alt="Export" size="small" Icon={IoDownloadOutline} />}
            buttonType="tertiary"
            sx={{
              cursor: "pointer",
              fontSize: "13px",
              border: 0,
              paddingRight: 1,
              paddingLeft: 1,
              "&:hover": {
                border: 0
              },
              "&:disabled": {
                border: 'none'
              }
            }}
          >
            Export CSV
          </CabButton>
        </CabTooltip>
      )}
      <CabButton
        icon={<CabIcon
          alt='Labels'
          size='small'
          Icon={IoTrashOutline}
        />}
        onClick={handleClearFilters}
        disabled={!tableFilter?.items.length}
        buttonType="tertiary"
        color="primary"
        sx={{
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: 500,
          border: 0,
          paddingRight: 1,
          paddingLeft: 1,
          "&:hover": {
            border: 0,
          },
          "&:disabled": {
            border: 'none'
          }
        }}
      >
        Clear filters
      </CabButton>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      {!polls && !reusable && (
        <CabButton
          onClick={onNewMeeting}
          icon={<CabIcon alt="Add" size="small" Icon={IoAdd} />}
          size="small"
          buttonType="tertiary"
          sx={{
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: 500,
            border: 0,
            paddingRight: 1,
            paddingLeft: 1,
            "&:hover": {
              border: 0
            },
            "&:disabled": {
              border: 'none'
            }
          }}
        >
          Add Record
        </CabButton>
      )}
    </Box>
  </Box>
));
MeetingLogToolbar.displayName = "MeetingLogToolbar";

const MeetingLogToolbarSlot =
  MeetingLogToolbar as unknown as NonNullable<DataGridProProps["slots"]>["toolbar"];

type LeadersFilterInputProps = GridFilterInputValueProps & {
  leaders: Leader[];
};

const LeadersFilterInput = ({ item, applyValue, leaders }: LeadersFilterInputProps) => {
  const labelId = `leaders-filter-${item.id ?? item.field}-label`;
  const inputId = `leaders-filter-${item.id ?? item.field}`;
  const value = Array.isArray(item.value) ? item.value : [];

  return (
    <FormControl fullWidth size="small" variant="outlined">
      <InputLabel id={labelId} shrink>Value</InputLabel>
      <CabDropdown<number>
        label="Value"
        inputId={inputId}
        size="small"
        multiple
        options={leaders.map((leader) => ({
          value: leader.id,
          label: `${leader.first_name} ${leader.last_name?.[0] ?? ""}.`
        }))}
        value={value}
        onChange={(e) => {
          const selected = e.target.value;
          applyValue({
            ...item,
            value: Array.isArray(selected) ? selected.map(Number) : []
          });
        }}
        overrides={{
          labelId,
          notched: true,
          sx: {
            width: "100%",
            height: 38,
            "& .MuiSelect-multiple": {
              paddingLeft: 0.5
            },
            "& .MuiSvgIcon-root": {
              height: 16,
              width: 16
            }
          }
        }}
      />
    </FormControl>
  );
};


const MeetingLogTable = ({
  meetings, rowCount, leaders, loading, paginationMode = 'client', filterMode = 'client', sortMode = 'client',
  onFetchMeetings, onStatusChange, onMeetingClick, sortableStatus = true, polls = false, reusable = false,
  onNewMeeting, onEditMeeting, onDeleteMeeting, onDuplicateMeeting, onShareMeeting, onCopyLink, onFetchMeetingSlots,
  onPriorityChange, onNotesChange, onShowQuestionsModalClick, emptyMessage, openSetScheduledDate, onSeeResults,
  fetchDataOnPaginateOrFilter, tableSort, tableFilter, columnVisibility, columnOrder, meetingSlots,
  onSetTableFilters, onSetColumnSort, onSetColumnVisibility, onColumnOrderChange, onSetIsTemplateParent,
  isExporting, onSetIsExporting,
  showTimesOfferedColumn = false, showSelectedScheduledColumn = false, showDateScheduledColumn = false
}: {
  meetings: MeetingInfo[]; leaders: Leader[]; loading: boolean; sortableStatus?: boolean; rowCount?: number;
  paginationMode?: 'client'|'server'; filterMode?: 'client'|'server'; sortMode?: 'client'|'server'; polls?: boolean;
  reusable?: boolean;
  fetchDataOnPaginateOrFilter: boolean;
  onStatusChange: (meeting: Meeting, status: number) => Promise<void>;
  onPriorityChange: (meeting: Meeting, priority: number) => Promise<void>;
  onNotesChange: (mtg: Meeting, notes: string) => void;
  onMeetingClick: (meetingId: number) => void;
  onNewMeeting: () => void;
  onEditMeeting: (id: Meeting['id']) => void;
  onSeeResults: (id: Meeting['id']) => void;
  onDeleteMeeting: (mtg: Meeting) => void;
  onDuplicateMeeting: (id: Meeting['id']) => void;
  onShareMeeting: (id: Meeting['id']) => void;
  onCopyLink: (id: Meeting['id']) => void;
  onFetchMeetingSlots: () => Promise<void>;
  onShowQuestionsModalClick: (questionsString: string) => void;
  emptyMessage: string;
  onFetchMeetings: (
    templateParentId?: number, page?: number, filterSortState?: FilterSortState
  ) => Promise<FetchReturn<FetchMeetingsResponse> | undefined>;
  openSetScheduledDate: (meeting: Meeting) => void;
  tableFilter: GridFilterModel;
  tableSort: GridSortModel;
  columnVisibility: GridColumnVisibilityModel;
  onSetTableFilters: (filters: GridFilterModel | undefined) => void;
  onSetColumnSort: (sort: GridSortItem[]) => void;
  onSetColumnVisibility: (model: GridColumnVisibilityModel) => void;
  onColumnOrderChange?: (newOrder: string[]) => void;
  onSetIsTemplateParent: (isTemplateParent: boolean) => void;
  meetingSlots: MeetingSlot[];
  columnOrder?: string[];
  isExporting: boolean;
  onSetIsExporting: (value: boolean) => void;
  showTimesOfferedColumn?: boolean;
  showSelectedScheduledColumn?: boolean;
  showDateScheduledColumn?: boolean;
}) => {
  const [headerLabel] = useCabinetText(['meeting-log-header']);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isFullyInitialized, setIsFullyInitialized] = useState(false);
  const [isLoadingChildrenForExport, setIsLoadingChildrenForExport] = useState(false);
  const [isExportReady, setIsExportReady] = useState(false);

  const gridApiRef = useGridApiRef();
  const [expandedRows, setExpandedRows] = useState(new Set<number>());

  const durationMinutesOperators = getGridNumericOperators().filter(
    (filter) => ["=", ">=", "<="].includes(filter.value)
  ).map((filter) => {
    filter.InputComponent = (props: GridFilterInputValueProps) => {
      return <Box sx={{ width: "100%" }}>
        <CabDurationPicker
          durationMinutes={props.item.value || 0}
          sx={{
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
              height: 38
            }
          }}
          onChange={(value) => {
            props.applyValue({...props.item, value: value});
          }}
        
        />
      </Box>;
    };
    return filter;
  });

  const onClickQuestions = (questionString: string, isTemplateParent: boolean) => {
    onSetIsTemplateParent(isTemplateParent);
    onShowQuestionsModalClick(questionString);
  };

  const getMeetingTimes = useCallback((meetingId: number, tz: string | null, recurringTimes: RecurringTimes | null) => {
    const includeSlots = meetingSlots.filter(s => s.meeting === meetingId && !s.is_exclude);
    const excludeSlots = meetingSlots.filter(s => s.meeting === meetingId && s.is_exclude);
    const timezone = allTimeZones.find((zone: TimeZone) => zone.name === tz);
    return getTimesOfferedText(includeSlots, excludeSlots, recurringTimes, timezone);
  }, [meetingSlots]);

  const renderTimes = useCallback((
    {row: { id, calendar_tz, recurring_times }}: GridRenderCellParams<Meeting>
  ): ReactNode => {
    return (
      <div>
        <CabTooltip 
          title={getMeetingTimes(id, calendar_tz, recurring_times)} 
          placement="bottom" wrapWithSpan>
          <Box sx={{
            display: 'flex'
          }}>
            See times...
          </Box>
        </CabTooltip>
      </div>
    );
  }, [getMeetingTimes]);

  const columns: GridColDef<Row>[] = useMemo(() => {
    const reusableCols: GridColDef<Row>[] = [
      {
        field: 'template_children',
        headerName: 'Is Template',
        valueGetter: (value, row, column, apiRef) => (
          row.template_parent ? '' : 'Yes'
        ),
        display: "flex",
      },
      {
        field: 'template_parent',
        headerName: 'Template ID',
        valueGetter: (value, row, column, apiRef) => value || row.id,
        display: "flex",
      },
      {
        field: 'internal_label',
        headerName: 'Internal Label',
        minWidth: 180,
        display: "flex",
        flex: 1,
        filterOperators: getGridStringOperators().filter(op => op.value === 'contains'),
        valueGetter: (value, row, column, apiRef) => {
          // If we are showing the reusable log, we only want to show internal label for the parent meetings
          if (reusable && !row.is_reusable) return '';

          // If the meeting has its own internal label, show it
          if (value) return value;
          
          // If the meeting has no internal label set, use the event title but fill replace template chips with text
          const templateVars = getTemplateVars(row.questions);

          const templateNameLookup = Object.entries(templateVars)
            .map(([k, v]) => ({ [k]: k.startsWith('answer') ? v.explanation : v.replaceText }))
            .reduce((a, b) => ({ ...a, ...b }), {});

          return transformSimpleTemplateToText(row.title, templateNameLookup);
        },
        renderCell: (params) => (
          <Box
            onClick={() => onMeetingClick(params.row.id)}
            sx={{
              marginTop: 0.5,
              marginBottom: 0.5,
              cursor: 'pointer',
              whiteSpace: 'normal',
              wordWrap: 'break-word'
            }}>
            {params.formattedValue}
          </Box>
        )
      }
    ];

    const pollCols: GridColDef<Row>[] = [
      {
        field: 'participant_responses',
        headerName: 'Responses',
        width: 90,
        display: "flex",
        type: 'number',
        filterOperators: getGridNumericOperators().filter(op => ['=', '<', '>'].includes(op.value)),
        valueGetter: (value, row, column, apiRef) => getNumResponses(Object.values(
          row.participants || {}
        ).map(participantItr => ({
          first_response_date: participantItr.first_response_date
        }))),
        renderCell: (params: GridRenderCellParams<Row>) => (
          `${params.value} / ${params.row.num_expected_participants || 0}`
        ),
      }
    ];

    const notPollsCols: GridColDef<Row>[] = [
      {
        field: 'questions',
        headerName: 'Questions',
        width: 180,
        disableColumnMenu: false,
        resizable: true,
        hideable: true,
        filterable: true,
        sortable: true,
        display: "flex",
        filterOperators: getGridStringOperators().filter(op => op.value === 'contains'),
        valueGetter: (value, row, column, apiRef) => (
          getQuestionsAndAnswers(value || {}, !row.template_parent)
        ),
        renderCell: (params: GridRenderCellParams<Row>) => (
          <Box
            sx={{
              marginTop: 0.5,
              marginBottom: 0.5,
              cursor: 'pointer',
              maxWidth: '150px'
            }}>
            {params.value ? (
              <Box onClick={() => onClickQuestions(params.value, !params.row.template_parent)}>
                <CabTooltip title={<Box sx={{
                  whiteSpace: 'pre-wrap'
                }}>{params.value}</Box>}>
                  <Typography
                    variant='body2'
                    noWrap
                    sx={{
                      maxWidth: '180px',
                      textOverflow: 'ellipsis'
                    }}>
                    {params.value}
                  </Typography>
                </CabTooltip>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: .25
                }}>
                <Typography variant='body2' sx={{
                  fontStyle: 'italic'
                }}>No Questions</Typography>
              </Box>
            )}
          </Box>
        ),
      }
    ];

    return [
      ...(reusable ? reusableCols : []),
      {
        field: 'title',
        headerName: 'Event Title',
        minWidth: 180,
        display: "flex",
        flex: reusable ? undefined : 1,
        filterOperators: getGridStringOperators().filter(op => op.value === 'contains'),
        valueGetter: (value, row, column, apiRef) => {
          if (row.title_booked) return row.title_booked;
          return value;
        },
        valueFormatter: (value, row, column, apiRef) => {
          if (value == null) return '';
          const templateVars = row.id ? getTemplateVars(apiRef.current.getRow(row.id).questions) : {};
          const templateNameLookup = Object.entries(templateVars)
            .map(([k, v]) => ({ [k]: v.replaceText }))
            .reduce((a, b) => ({ ...a, ...b }), {});
          return transformSimpleTemplateToText(value, templateNameLookup);
        },
        // filterOperators: containsOperator,
        renderCell: (params) => (
          <Box
            onClick={() => onMeetingClick(params.row.id)}
            sx={{
              marginTop: 0.5,
              marginBottom: 0.5,
              cursor: 'pointer',
              whiteSpace: 'normal',
              wordWrap: 'break-word'
            }}>
            <CabTextTokenDisplay templateVars={getTemplateVars(params.row.questions)}>
              {params.row.status !== MeetingStatus.PENDING
                && params.row.title_booked ? params.row.title_booked : params.value || ''}
            </CabTextTokenDisplay>
          </Box>
        )
      },
      ...(polls ? pollCols : []),
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        display: "flex",
        type: 'singleSelect',
        sortable: sortableStatus,
        valueFormatter: (value: {label: string, value: number}, row, column, apiRef) => value.label,
        valueGetter: (value, row, column, apiRef) => ({
          label: row.is_reusable 
            ? REUSABLE_MEETING_STATUS_LABELS[value] 
            : MEETING_STATUS_LABELS[value],
          value: row.is_reusable ? -value : value,
        }),
        valueOptions: Object.entries(MEETING_STATUS_LABELS)
          .map(([id, label]) => ({
            value: Number(id),
            label: label,
          }))
          .concat(
            Object.entries(!polls ? REUSABLE_MEETING_STATUS_LABELS : {})
              .map(([id, label]) => ({
                value: -Number(id),
                label: label,
              }))
          ),
        sortComparator: (a, b) => a.label.localeCompare(b.label),
        renderCell: (params) => (
          <CabDropdown
            value={Math.abs(params.value.value)}
            onChange={e => onStatusChange(params.row, Number(e.target.value))}
            options={
              Object.keys(params.row.is_reusable 
                ? REUSABLE_MEETING_STATUS_LABELS 
                : MEETING_STATUS_LABELS).map((statusId) => {
                const id = Number(statusId);
                return {
                  value: id,
                  label: params.row.is_reusable ? REUSABLE_MEETING_STATUS_LABELS[id] : MEETING_STATUS_LABELS[id],
                };
              })
            }
            overrides={{
              renderValue: (value: unknown) => {
                const id = Number(value);
                const color = statusColors[id] || colors.neutral;
                const label = params.row.is_reusable ? REUSABLE_MEETING_STATUS_LABELS[id] : MEETING_STATUS_LABELS[id];
                return (
                  <CabToggleChip
                    chipColor={color}
                    sx={{ color: color === colors.neutral ? undefined : colors.white900, width: '150px' }}
                    label={label}
                    selected={true}
                  />
                );
              },
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                border: 0,
              },
              "& .MuiOutlinedInput-input": {
                paddingLeft: 0.5,
              }
            }}
          />
        )
      },
      {
        field: 'leaders',
        headerName: headerLabel,
        width: 150,
        display: "flex",
        type: 'singleSelect',
        valueOptions: leaders.map((l) => ({ value: l.id, label: `${l.first_name} ${l.last_name[0]}.` })),
        valueFormatter: (value: number[], row, column, apiRef) => (
          leaders.filter(leader => value && value.includes(leader.id))
            .map(leader => `${leader.first_name} ${leader.last_name}`).join(", ")
        ),
        renderCell: ({ value }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column"
            }}>
            {leaders.filter(leader => value && value.includes(leader.id)).map((leader: Leader) => (
              <Box
                key={leader.id}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1
                }}>
                <Box style={{ backgroundColor: leader.color, height: 10, width: 10, borderRadius: 5 }} />
                {`${leader.first_name} ${leader.last_name[0]}.`}
              </Box>
            ))}
          </Box>
        ),
        sortComparator: (a, b) => (
          a.map((l: Leader) => `${l.first_name} ${l.last_name}`).join(',')
            .localeCompare(b.map((l: Leader) => `${l.first_name} ${l.last_name}`).join(','))
        ),
        filterOperators: [/*...getGridSingleSelectOperators(),*/ {
          label: 'contains',
          value: 'eq',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) {
              return null;
            }
            return (value, row, column, apiRef) => {
              const rowValues = value || [];
              return filterItem.value.every((val: number) => rowValues.includes(val));
            };
          },
          InputComponent: (props: GridFilterInputValueProps) => (
            <LeadersFilterInput {...props} leaders={leaders} />
          )
        }],
      }, 
      {
        field: 'participants',
        headerName: 'Attendees',
        width: 190,
        display: "flex",
        valueGetter: (value: Meeting['participants'], row, column, apiRef) => Object.values(
          value || {}
        ).map(participantItr => participantItr.name ? `${participantItr.name} <${participantItr.email}>` 
          : participantItr.email),
        valueFormatter: (value: string[], row, column, apiRef) => (
          value.join(", ")
        ),
        renderCell: (params: GridRenderCellParams<Row>) => {
          const [first, ...rest] = params.value;
          if (!first) return null;
          return (
            <CabTooltip title={params.value.join('\n')} wrapWithSpan spanSx={{ overflowX: 'clip' }}>
              <Box sx={{ textOverflow: 'ellipsis', overflowX: 'clip' }}>
                {first}
              </Box>
              {rest.length > 0 && (
                <Box sx={{ textOverflow: 'ellipsis', overflowX: 'clip' }}>
                  + {rest.length} more
                </Box>
              )}
            </CabTooltip>
          );
        },
      },
      {
        field: 'duration_minutes',
        headerName: 'Duration',
        width: 100,
        display: "flex",
        filterOperators: durationMinutesOperators,
        valueFormatter: (value, row, column, apiRef) =>  
          Duration.fromObject({minute: value}).toFormat('hh:mm')
      },
      ...(showTimesOfferedColumn ? [{
        field: 'timesOffered',
        type: 'string',
        headerName: 'Times Offered',
        width: 165,
        display: "flex" as const,
        editable: false,
        filterable: false,
        renderCell: renderTimes,
      }] satisfies GridColDef<Row>[] : []),
      {
        field: 'priority',
        headerName: 'Priority',
        type: 'singleSelect',
        width: 130,
        display: "flex",
        valueFormatter: (value: {label: string, value: number}, row, column, apiRef) => value.label,
        valueGetter: (value, row, column, apiRef) => ({
          label: MEETING_PRIORITY_LABELS[value],
          value: value,
        }),
        valueOptions: Object.entries(MEETING_PRIORITY_LABELS)
          .map(([id, label]) => ({ value: Number(id), label: label })),
        sortComparator: (a, b) => a.value - b.value,
        renderCell: (params) => (
          <CabDropdown
            value={Math.abs(params.value.value)}
            onChange={e => onPriorityChange(params.row, Number(e.target.value))}
            options={
              Object.keys(MEETING_PRIORITY_LABELS).map((priorityId) => {
                const id = Number(priorityId);
                return {
                  value: id,
                  label: MEETING_PRIORITY_LABELS[id],
                };
              })
            }
            overrides={{
              renderValue: (value: unknown) => {
                const id = Number(value);
                const color = priorityColors[id] || colors.neutral;
                const label = MEETING_PRIORITY_LABELS[id];
                return (
                  <CabToggleChip
                    chipColor={color}
                    sx={{ color: color === colors.neutral ? undefined : colors.white900,  width: '140px' }}
                    label={label}
                    selected
                  />
                );
              },
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                border: 0
              },
              "& .MuiOutlinedInput-input": {
                paddingLeft: 0.5,
              }
            }}
          />
        )
      },
      ...(showSelectedScheduledColumn ? [
        {
          field: 'selected_scheduled_date',
          headerName: 'Date Confirmed',
          description: 'The start date of the calendar event for this meeting',
          sortable: true,
          filterable: false,
          width: 170,
          display: "flex",
          valueGetter: (value: string | null | undefined, row: Row) => {
            const val = row.selected_scheduled_time;
            if (val == null) return null;
            return DateTime.fromISO(val, row.calendar_tz ? { zone: row.calendar_tz } : undefined);
          },
          valueFormatter: (value: DateTime) => {
            if (value == null) return '';
            else {
              return value.toFormat("MM/dd/yyyy");
            }
          },
          renderCell: (params: GridRenderCellParams<Row>) => {
            if (params.row.is_reusable) return null;
            return (
              <Typography
                sx={{
                  fontSize: "14px",
                  fontStyle: !params.row.selected_scheduled_time ? "italic" : undefined,
                  cursor: 'pointer',
                }}
                onClick={() => openSetScheduledDate(params.row)}
              >
                {params.formattedValue ? params.formattedValue : "Set Date Confirmed"}
              </Typography>
            );
          }
        },
        {
          field: 'selected_scheduled_time',
          headerName: 'Time Confirmed',
          description: 'The start time of the calendar event for this meeting',
          sortable: true,
          filterable: false,
          width: 170,
          display: "flex",
          valueGetter: (value: string | null | undefined, row: Row) => {
            if (value == null) return null;
            return DateTime.fromISO(value, row.calendar_tz ? { zone: row.calendar_tz } : undefined);
          },
          valueFormatter: (value: DateTime) => {
            if (value == null) return '';
            else {
              return value.toFormat("t ZZZZ ('GMT'Z)");
            }
          },
          renderCell: (params: GridRenderCellParams<Row>) => {
            if (params.row.is_reusable) return null;
            return (
              <Typography
                sx={{
                  fontSize: "14px",
                  fontStyle: !params.row.selected_scheduled_time ? "italic" : undefined,
                  cursor: 'pointer',
                }}
                onClick={() => openSetScheduledDate(params.row)}
              >
                {params.formattedValue ? params.formattedValue : "Set Time Confirmed"}
              </Typography>
            );
          }
        }
      ] satisfies GridColDef<Row>[] : []),
      ...(showDateScheduledColumn ? [{
        field: 'date_scheduled',
        sortable: false,
        filterable: false,
        headerName: 'Confirmed On',
        description: 'The date that a time was selected for this meeting',
        width: 140,
        type: 'date',
        display: "flex",
        valueGetter: (value?: string) => value ? DateTime.fromISO(value).toJSDate() : null,
        valueFormatter: (value?: Date) => value ? DateTime.fromJSDate(value).toFormat('MM/dd/yyyy') : ''
      }] satisfies GridColDef<Row>[] : []),
      {
        field: 'create_date',
        headerName: 'Created On',
        description: 'The date this meeting was initiated on Cabinet',
        width: 140,
        display: "flex",
        type: 'date',
        filterOperators: getGridDateOperators().filter(op => op.value === 'onOrBefore' || op.value === 'onOrAfter'),
        valueGetter: (value, row, column, apiRef) => DateTime.fromISO(value).toJSDate(),
        valueFormatter: (value, row, column, apiRef) => DateTime.fromJSDate(value).toFormat('MM/dd/yyyy'),
      },
      {
        field: 'create_user',
        headerName: 'Created By',
        width: 150,
        display: "flex",
        filterOperators: getGridStringOperators().filter(op => op.value === 'contains'),
        // filterOperators: containsOperator,
        valueGetter: (value: Meeting['create_user'] | undefined, row, column, apiRef) => (
          value ? `${value.first_name} ${value.last_name?.[0]}.` : ''
        ),
      },
      {
        field: 'notes',
        headerName: 'Internal Notes',
        width: 180,
        display: "flex",
        disableColumnMenu: true,
        resizable: true,
        hideable: true,
        filterable: true,
        sortable: true,
        filterOperators: getGridStringOperators().filter(op => op.value === 'contains'),
        valueFormatter: (value, row, column, apiRef) => {
          return value || '';
        },
        renderCell: (params) => (
          <Box
            onClick={() => onNotesChange(params.row, params.row.notes || '')}
            sx={{
              marginTop: 0.5,
              marginBottom: 0.5,
              cursor: 'pointer',
              maxWidth: '100%'
            }}>
            {params.value ? (
              <CabTooltip title={<Box sx={{
                whiteSpace: 'pre-wrap'
              }}>{params.value}</Box>}>
                <Typography
                  variant='body2'
                  sx={{
                    maxWidth: '100%',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                  {params.value}
                </Typography>
              </CabTooltip>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: .25
                }}>
                <Typography>+</Typography>
                <Typography variant='body2' sx={{
                  fontStyle: 'italic'
                }}>Add Notes</Typography>
              </Box>
            )}
          </Box>
        )
      },
      ...(!polls ? notPollsCols : []),
      {
        field: 'locations',
        headerName: 'Location(s)',
        disableColumnMenu: true,
        minWidth: 130,
        display: "flex",
        valueGetter: (value, row, column, apiRef) => (
          row.status === MeetingStatus.SCHEDULED ? row.locations_booked : value
        ),
        valueFormatter: (value: Meeting['locations'], row, column, apiRef) => value?.join(', ')
      },
      {
        field: '',
        headerName: '',
        minWidth: 0,
        display: "flex",
        flex: 1,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        filterable: false,
        sortable: false,
      },
      {
        field: 'Options',
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        headerName: '',
        display: "flex",
        width: 45,
        minWidth: 45,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
          <ActionMenu
            isPoll={polls}
            onEdit={() => onEditMeeting(params.row.id)}
            onSeeResults={() => onSeeResults(params.row.id)}
            onDelete={() => onDeleteMeeting(params.row)}
            onDuplicate={() => onDuplicateMeeting(params.row.id)}
            onShare={() => onShareMeeting(params.row.id)}
            onCopyLink={() => onCopyLink(params.row.id)}
            sx={{width: 20}} 
            target={<CabIcon
              Icon={IoEllipsisHorizontal}
              sx={{textAlign: "right", fontSize: 20}} 
            />}
          />
        ),
      },
    ];
    // needs deep comparison
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerLabel, JSON.stringify(leaders), sortableStatus, polls,
    onDeleteMeeting, onDuplicateMeeting, onEditMeeting, onMeetingClick, onShareMeeting, onCopyLink, onStatusChange,
    onSeeResults]);

  const orderedColumns = useMemo(() => {
    if (!columnOrder?.length) return columns;
    const currentFieldNames = new Set(columns.map(c => c.field));
    const savedFieldNames = new Set(columnOrder);
    const validSavedFields = columnOrder.filter(field =>
      currentFieldNames.has(field) && field !== '' && field !== 'Options'
    );
    const newFields = columns
      .filter(col =>
        !savedFieldNames.has(col.field) && col.field !== '' && col.field !== 'Options'
      )
      .map(col => col.field);
    const finalOrder = [...validSavedFields, ...newFields];
    const visibleOrderedColumns = finalOrder
      .map(fieldName => columns.find(col => col.field === fieldName))
      .filter(Boolean) as GridColDef<Row>[];
    const trailingColumns = columns.filter(col => col.field === '' || col.field === 'Options');
    return [...visibleOrderedColumns, ...trailingColumns];
  }, [columns, columnOrder]);

  const [page, setPage] = useState(0);
  // const [filterSortState, setFilterSortState] = useState<FilterSortState>({});
  const [meetingIds, setMeetingIds] = useState<Meeting['id'][]|undefined>();
  const pageSize = 20;

  const initialState: GridInitialStatePro = useMemo(() => {
    const leftPinnedColumns = reusable 
      ? ['__tree_data_group__', 'internal_label', 'title']
      : ['title'];
    const base: GridInitialStatePro = {
      pinnedColumns: { 
        left: leftPinnedColumns,
        right: ['Options'] 
      },
      pagination: { paginationModel: { page: 0, pageSize }},
    };
    if (columnOrder?.length) {
      base.columns = { orderedFields: columnOrder.filter(f => f !== '' && f !== 'Options') };
    }
    return base;
  }, [pageSize, reusable, columnOrder]);

  const rows = useMemo(() => {
    let computedRows = meetingIds
      ?.map(id => meetings.find(mtg => mtg.id === id))
      // always include children of expanded rows
      .concat(meetings.filter(mtg => mtg.template_parent && expandedRows.has(mtg.template_parent)))
      .filter((mtg) => mtg !== undefined) as Row[] || meetings;
    
    computedRows = computedRows
      // don't include the child unless the parent exists in the rows. Otherwise, we will get errors
      .filter(row => reusable && row.template_parent && !computedRows.find(r => r.id === row.template_parent)
        ? false
        : true)
      .map(row => ({
        ...row,
        hierarchy: row.template_parent
          ? [row.template_parent.toString(), row.id.toString()]
          : [row.id.toString()]
      }));

    // add placeholder rows
    return [
      ...computedRows,
      ...computedRows
        .filter(row => (row.template_children?.length || 0) > 0
          && (!computedRows.find(r => r.hierarchy.length > 1 && r.hierarchy[0] === row.id.toString())
            && !expandedRows.has(row.id)))
        .map(row => createPlaceholderRow(row.id)),
    ];
  }, [meetingIds, meetings, expandedRows, reusable]);


  const handleRowsExpansionChange = useCallback(async (nodes: (GridGroupNode | GridTreeNode)[]) => {
    const api = gridApiRef.current;
    if (!api) return;

    const fetchPromises: Promise<FetchReturn<FetchMeetingsResponse> | undefined>[] = [];
    const newSet = new Set(expandedRows);

    nodes.forEach(node => {
      const row = api.getRow(node.id) as Row | null;
      if ((node.type === "group" && !node.childrenExpanded) || !row) {
        if (row) {
          newSet.delete(row.id);
        }
      } else {
        const fetchPromise = onFetchMeetings(row.id, undefined, {
          sortModel: tableSort,
          filterModel: tableFilter,
        });
        fetchPromises.push(fetchPromise);
        newSet.add(row.id);
      }
    });
    await Promise.all(fetchPromises);
    setExpandedRows(newSet);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApiRef, expandedRows, tableFilter, tableSort]);

  useEffect(() => {
    const handleRowExpansionChange: GridEventListener<"rowExpansionChange"> = async (node, evt, details) => {
      handleRowsExpansionChange([node]);
    };

    const api = gridApiRef.current;
    if (!api) return undefined;

    return api.subscribeEvent(
      "rowExpansionChange",
      handleRowExpansionChange
    );
  }, [gridApiRef, handleRowsExpansionChange]);

  // TODO: this is being called twice on polls
  const updateMeetings = useCallback(async (loadAllForExport?: boolean) => {
    if (fetchDataOnPaginateOrFilter) {
      // We must convert our local status value (negative for reusable, positive otherwise) for back-end to understand
      // We must also detect the type of status we are filtering by and add additional is_reusable filter if necessary
      // to get the correct results.
      const items = tableFilter?.items.map(item => {
        if (item.field === 'status' && item.value !== undefined) {
          if (typeof item.value === 'number') {
            return { ...item, value: Math.abs(item.value) };
          }
          return { ...item, value: item.value.map((v: number) => Math.abs(v)) };
        }
        return item;
      }) || [];

      const hasNormalStatus = !!tableFilter?.items.find(
        item => item.field === 'status' && (
          (typeof item.value === 'number' && item.value > 0) ||
          (Array.isArray(item.value) && item.value.some((v: number) => v > 0))
        )
      );
      const hasReusableStatus = !!tableFilter?.items.find(
        item => item.field === 'status' && (
          (typeof item.value === 'number' && item.value < 0) ||
          (Array.isArray(item.value) && item.value.some((v: number) => v < 0))
        )
      );
      if (!items.find(item => item.field === 'is_reusable')) {
        if (hasNormalStatus && !hasReusableStatus) {
          items.push({ field: 'is_reusable', value: 'false', operator: 'is' });
        } else if (hasReusableStatus && !hasNormalStatus) {
          items.push({ field: 'is_reusable', value: 'true', operator: 'is' });
        }
      }

      const res = await onFetchMeetings(undefined, loadAllForExport ? undefined : page, {
        sortModel: tableSort,
        filterModel: { ...tableFilter, items }
      });
      if (res && 'data' in res && 'data' in res.data) {
        setMeetingIds(Object.keys(res.data.data.meetings).map(Number));
      }
    }
  }, [page, onFetchMeetings, fetchDataOnPaginateOrFilter, tableFilter, tableSort]);

  useEffect(() => {
    // Exporting data manually updates as-needed, and this breaks that pattern
    if (!isExporting) {
      updateMeetings();
    }
  }, [updateMeetings, isExporting]);

  const handlePageChange = useCallback(async (pg: number) => {
    setPage(pg);
    setExpandedRows(new Set());
  }, []);

  const handleSaveTableFilters = useCallback((filters: GridFilterModel) => {
    onSetTableFilters(filters);
    setPage(0);
    setExpandedRows(new Set());
  }, [onSetTableFilters]);

  const handleSaveTableSort = useCallback((sort: GridSortItem[]) => {
    onSetColumnSort(sort);
    setPage(0);
    setExpandedRows(new Set());
  }, [onSetColumnSort]);

  const handleColumnOrderChange = useCallback<GridEventListener<'columnOrderChange'>>((params, event, details) => {
    if (onColumnOrderChange && details.api) {
      const allColumns = details.api.getAllColumns();
      const newOrder = allColumns
        .filter(col => col.field !== '' && col.field !== 'Options')
        .map(col => col.field);
      onColumnOrderChange(newOrder);
    }
    void params;
    void event;
  }, [onColumnOrderChange]);

  useMountEffect(() => {
    // NOTE: need this to fix resize bug w/ row height and ensure loading state
    setTimeout(() => setIsFullyInitialized(true), 500);

    if (handlePageChange) {
      handlePageChange(0);
      onFetchMeetingSlots();
    } else {
      onFetchMeetingSlots();
    }
  });

  const getRowHeight = useCallback((): 'auto' => 'auto', []);

  const handleClearFilters = useCallback(() => {
    gridApiRef.current?.setFilterModel({items: []});
    onSetTableFilters(undefined);
    setPage(0);
    setExpandedRows(new Set());
  }, [gridApiRef, onSetTableFilters]);

  // ---START Functions for exporting all data to CSV---
  // These functions exists to make sure we load all data before exporting 
  // and then return the table to its previous state
  // NOTE: The useEffect triggering the second half allows a render cycle to actually populate the data in the grid.
  //       Doing this as one function exports old data (whichever page is currently loaded)
  const startExportMeetings = useCallback(async () => {
    onSetIsExporting(true);
    await updateMeetings(true);
    setIsLoadingChildrenForExport(true);
    //setIsExportReady(true);
  }, [updateMeetings, onSetIsExporting]);

  const loadReusableChildrenForExport = useCallback(async () => {
    if (reusable) {
      const api = gridApiRef.current;
      if (!api) return;

      const rowIds = exportExcludeDecimalIds(gridApiRef);
      const rowNodes = rowIds.map(id => api.getRowNode(id));
      const nodesToExpand: GridTreeNode[] = [];
      rowNodes.forEach((node) => {
        if (node && node.type === "group" && !node.childrenExpanded) {
          node.childrenExpanded = true;
          nodesToExpand.push(node);
        }
      });
      await handleRowsExpansionChange(nodesToExpand);
    }
  }, [reusable, gridApiRef, handleRowsExpansionChange]);

  // The export coming AFTER the updateMeetings(false) call serves 2 purposes:
  //  1) It allows a little extra time to make sure the table has fully loaded all the new rows
  //  2) It prevents the export from downloading too early (while the table is frozen loading rows)
  // NOTE: Despite how it looks, the data is populated as of the previous render cycle, so even though the NEW data 
  //    is fetched, the data exported is the data from the previous updateMeetings(true) call
  const finishExportMeetings = useCallback(async () => {
    await updateMeetings(false);

    // Export the previously rendered rows (full data)
    const api = gridApiRef.current;
    if (!api) return;

    const exportProps = gridExportProps(reusable);
    api.exportDataAsCsv(exportProps.csvOptions);

    // Remove expanded rows from the state
    if (reusable) {
      setExpandedRows(new Set());
    }
    onSetIsExporting(false);
  }, [gridApiRef, reusable, updateMeetings, onSetIsExporting]);

  useEffect(() => {
    if (isLoadingChildrenForExport) {
      loadReusableChildrenForExport().finally(() => {
        setIsLoadingChildrenForExport(false);
        setIsExportReady(true);
      });
    }
  }, [isLoadingChildrenForExport, loadReusableChildrenForExport]);

  useEffect(() => {
    if (isExportReady) {
      setIsExportReady(false);
      finishExportMeetings();
    }
  }, [isExportReady, finishExportMeetings]);
  // ---END Functions for exporting all data to CSV---

  const slots = useMemo(() => ({
    toolbar: MeetingLogToolbarSlot,
    noRowsOverlay: () => (
      <NoRowOverlay emptyMessage={` There are no ${emptyMessage} that match your filters.`} msTimeout={6000} />
    ),
    noResultsOverlay: () => (
      <Stack
        sx={{
          height: "100%",
          alignItems: "center",
          justifyContent: "center"
        }}>
        {` There are no ${emptyMessage} that match your filters.`}
      </Stack>
    ),
  }), [emptyMessage]);

  const isGroupExpandedByDefault = useCallback((node: GridGroupNode) => {
    // Getting "getRow not a function" error on init sometimes without this
    if (!gridApiRef.current?.getRow) {
      return false;
    }
    const row = gridApiRef.current.getRow(node.id) as Row | null;
    if (row) return expandedRows.has(row.id);
    // NOTE: for some reason the node id will be returned with this prefix followed by the actual ID.
    // This condition will extract the real ID.
    else if (node.id.toString().startsWith('auto-generated-row-null/')) {
      const rowId = Number(node.id.toString().replace('auto-generated-row-null/', ''));
      return expandedRows.has(rowId);
    }
    return false;
  }, [gridApiRef, expandedRows]);

  const getTogglableColumns = useCallback((cols: GridColDef[]) => {
    return cols
      .filter(c => !['template_parent', 'template_children', '__tree_data_group__', 'Options'].includes(c.field))
      .map(c => c.field);
  }, []);

  const slotProps: GridProSlotProps = useMemo(() => ({
    columnsManagement: {
      getTogglableColumns: getTogglableColumns
    },
    toolbar: {
      polls,
      reusable,
      tableFilter,
      onNewMeeting,
      startExportMeetings,
      handleClearFilters
    } as unknown as NonNullable<DataGridProProps["slotProps"]>["toolbar"]
  }), [
    getTogglableColumns, handleClearFilters, onNewMeeting, polls, reusable, startExportMeetings, tableFilter
  ]);

  const paginationModel = useMemo(() => ({
    page: page,
    pageSize,
  }), [page]);

  const treeData = reusable;

  const pageSizeOptions = useMemo(() => [pageSize], [pageSize]);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    handlePageChange(model.page);
  }, [handlePageChange]);

  // NOTE: Make absolutely sure that all non-primitive props are static or memoized!
  return (
    <>
      <StyledDataGrid
        isGroupExpandedByDefault={isGroupExpandedByDefault}
        treeData={treeData}
        getTreeDataPath={reusable ? getTreeDataPath : undefined}
        groupingColDef={groupingColDef}
        apiRef={gridApiRef}
        getRowClassName={reusable ? getRowClassNameReusable : undefined}
        initialState={initialState}
        filterModel={tableFilter}
        sortModel={tableSort}
        columnVisibilityModel={columnVisibility}
        // NOTE: There used to be a bug where the height of each row would increase over time. 
        //       Delaying the rendering of rows using isFullyInitialized was necessary to fix this.
        //       If the problem returns, this can be uncommented.
        //rows={!isFullyInitialized ? [] : rows}
        loading={loading}
        rows={rows}
        columns={orderedColumns}
        showToolbar={!!slots?.toolbar}
        slots={slots}
        pagination
        paginationModel={paginationModel}
        // paginationMode={paginationMode}chore/sc-11382/data-grid-components-are-wrapped-in-memo
        onPaginationModelChange={handlePaginationModelChange}
        // setting rowCount with paginationMode set to "client" has no effect and throws a nasty error
        paginationMode={paginationMode}
        rowCount={paginationMode === 'server' ? rowCount : undefined}
        // setting filterMode to "server" while "treeData" is true causes an error
        filterMode={treeData ? "client" : filterMode}
        // sortingMode={sortMode}
        sortingMode={'client'}
        slotProps={slotProps}
        onSortModelChange={handleSaveTableSort as (m: GridSortModel) => void}
        onFilterModelChange={handleSaveTableFilters}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityModelChange={onSetColumnVisibility}
        pageSizeOptions={pageSizeOptions}
        disableRowSelectionOnClick
        disableVirtualization
        getRowHeight={getRowHeight}
        sx={isExporting ? {visibility: "hidden", zIndex: -1} : undefined}
      />
      {isExporting && <Box sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.white800,
        zIndex: 100000000,
      }}>
        <Typography variant='h3' sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CabSpinner scale={1.5} sx={{ marginRight: 2 }}/>
          Exporting your meetings - this may take a while.
        </Typography>
      </Box>}
    </>
  );
};

export default MeetingLogTable;

const statusColors: Record<string, string> = {
  '1': colors.neutral,
  '2': colors.greenPrimary,
  '3': darken(colors.coralPrimary, 0.05),
  '4': darken(colors.coralPrimary, 0.05),
  '5': darken(colors.coralPrimary, 0.05),
};

const priorityColors: Record<string, string> = {
  '3': lighten(colors.redError, 0.3),
  '6': colors.coralLight,
  '9': colors.blueLight
};


export type FilterSortState = {
  filterModel?: GridFilterModel;
  sortModel?: GridSortModel;
};

const exportExcludeDecimalIds = (apiRef: Parameters<typeof gridFilteredSortedRowIdsSelector>[0]) => {
  return gridFilteredSortedRowIdsSelector(apiRef).filter(id => Number(id) <= 0 || Number(id) >= 1);
};

const gridExportProps = (isReusable: boolean): GridToolbarExportProps => ({
  csvOptions: {
    fields: [
      'template_children', 'template_parent',
      ...(isReusable ? ['internal_label'] : []),
      "title", 'participant_responses', "status", "leaders", "participants", "duration_minutes", "priority",
      "selected_scheduled_date", 'selected_scheduled_time', "create_date", "create_user", "notes", 'questions',
      'locations'
    ],
    fileName: `Cabinet_Meetings_${new Date().toLocaleDateString('en')}_${new Date().toLocaleTimeString('en')}`,
    getRowsToExport: ({ apiRef }) => {
      // this filters out placeholder rows used for grouping which will have a decimal ID
      return exportExcludeDecimalIds(apiRef);
    },
  },
  printOptions: {
    disableToolbarButton: true,
  },
});

const CustomGridTreeDataGroupingCell = (props: GridRenderCellParams) => {
  const { id, field, rowNode, row } = props;
  const apiRef = useGridApiContext();
  const filteredDescendantCount = row.template_children?.length || 0;
  const isChild = rowNode.depth > 0;

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (rowNode.type !== 'group') {
      return;
    }

    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  return (
    <Box>
      <div>
        {!isChild && (
          <CabButton
            onClick={handleClick}
            disabled={filteredDescendantCount === 0}
            buttonType="text"
            size='small'
            icon={<CabIcon
              Icon={rowNode.type === 'group' && rowNode.childrenExpanded ? IoChevronDown : IoChevronForward}
            />}
            sx={{
              paddingLeft: 0, minWidth: 30, paddinRight: 8,
              '& .MuiButton-startIcon': { margin: 0 },
            }}
          >
            {`(${filteredDescendantCount})`}
          </CabButton>
        )}
      </div>
    </Box>
  );
};


const groupingColDef: DataGridProProps['groupingColDef'] = {
  headerName: '',
  renderCell: (params) => <CustomGridTreeDataGroupingCell {...params} />,
  width: 60,
  hideable: false,
  display: 'flex',
};

const StyledDataGrid = styled(CabDataGrid, { label: 'DataGridPro' })(({ theme }) => ({
  position: 'absolute', top: 0, bottom: 0, left: 0, right: 0,
  '& .reusable-meeting-child': {
    backgroundColor: colors.black100
  },
  '& .MuiDataGrid-row': {
    minHeight: '52px !important',
  }
})) as typeof DataGridPro;

const getTreeDataPath: DataGridProProps["getTreeDataPath"] = (row) => row.hierarchy;

const getRowClassNameReusable: DataGridProProps["getRowClassName"] = (params) => params.row.template_parent 
  ? 'reusable-meeting-child' : '';

const createPlaceholderRow = (parentId: number): Row => ({
  id: Number(`0.${parentId}`),
  hierarchy: [parentId.toString(), `placeholder-loading-${parentId}`],
  participantsInfo: [],
  slots: [],
  locations: [],
  create_user: { id: 0, first_name: '', last_name: '', email: '' },
  title: "",
  title_booked: null,
  calendar_tz: null,
  secondary_tz: null,
  copy_tz: null,
  secondary_copy_tz: null,
  date_scheduled: null,
  create_date: "2022-04-17T22:36:36.781260Z",
  created_at: "2022-04-17T22:36:36.781260Z",
  updated_at: null,
  status: MeetingStatus.PENDING,
  hold_calendars: [],
  prevent_conflict: true,
  event_start_time: null,
  event_id: null,
  executive_hold: true,
  error_count: 0,
  show_voter_names: true,
  allow_add_participants: true,
  enable_public_attendee_list: false,
  allow_reschedule_cancel: false,
  duration_minutes: 0,
  rooms: [],
  rooms_data: {},
  location_presets: [],
  location_presets_data: {},
  leaders: [],
  questions: {},
  auto_merge_slots: true,
  is_reusable: false,
  recurring_times: null,
  priority: MeetingPriority.MEDIUM,
  start_delay_minutes: 60,
  files: [],
  selected_scheduled_time: null,
  buffer_start_minutes: 0,
  buffer_end_minutes: 0,
  send_participant_reminders: false,
  participant_reminder_minutes: 24 * 60,
});
